import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Badge } from "@/components/ui/PageHeader";
import { TextAreaField } from "@/components/ui/FormFields";
import { useAuth } from "@/context/AuthContext";
import * as voices from "@/api/voices";
import type { VoiceStory, VoiceUpdate, VoiceAction, StoryReaction } from "@/api/voices";

// ═══════════════════════════════════════════════════════════════════
// The four lanes hanging off a voice.
//
// They are four and not one thread because they answer different questions,
// and collapsing them is how a community board turns into a comment section:
//
//   Stands   · who agrees, and how — a stance, optionally a chip
//   Stories  · "this happened to me too" — the lived evidence
//   Updates  · what has since become KNOWN — official word, expert, news
//   Actions  · what a reader can DO, and how many already did
//
// Plus two things that are about the voice rather than in it: the engagement
// stats (the shape of attention, not its size) and the intent, which is
// editable after the fact because why someone spoke changes what a good
// response looks like — and posters get that wrong at 2am.
// ═══════════════════════════════════════════════════════════════════

const LANES = ["stands", "stories", "updates", "actions", "stats"] as const;
type Lane = (typeof LANES)[number];

export default function VoiceLanes({ voiceId }: { voiceId: string }) {
  const [lane, setLane] = useState<Lane>("stands");

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <div className="flex gap-1 flex-wrap mb-4 border-b border-gray-200 pb-3">
        {LANES.map((l) => (
          <button
            key={l}
            onClick={() => setLane(l)}
            className={`px-3 py-1.5 rounded-lg text-sm capitalize ${
              lane === l ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {lane === "stands" && <Stands voiceId={voiceId} />}
      {lane === "stories" && <Stories voiceId={voiceId} />}
      {lane === "updates" && <Updates voiceId={voiceId} />}
      {lane === "actions" && <Actions voiceId={voiceId} />}
      {lane === "stats" && <Stats voiceId={voiceId} />}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-gray-500">{children}</p>;
}

function Err({ e }: { e: unknown }) {
  if (!e) return null;
  return (
    <p className="text-sm text-red-700 mt-2">
      {e instanceof Error ? e.message : "That did not work"}
    </p>
  );
}

// ── Stands ─────────────────────────────────────────────────────────
// Read-only here; standing itself is the button in the page header.

function Stands({ voiceId }: { voiceId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["voice-stands", voiceId],
    queryFn: () => voices.listVoiceStands(voiceId, { limit: 25 }),
  });

  if (isLoading) return <Empty>Loading…</Empty>;
  const stands = data?.data.stands ?? [];
  const hidden = data?.data.identitiesHidden ?? false;
  const composition = data?.data.composition ?? {};
  const total = data?.data.meta.total ?? 0;

  // WITHHELD is not the same as EMPTY, and saying "nobody stood with this"
  // over three withheld stands is a false statement about the one surface
  // where being named is the risk. On a sensitive or contested post the
  // server sends the composition and no identities; say exactly that.
  if (hidden) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3">
        <p className="text-sm font-medium text-amber-900">
          {total} {total === 1 ? "person stands" : "people stand"} with this. Names are
          withheld.
        </p>
        <p className="mt-1 text-xs text-amber-800">
          On a sensitive or contested post, appearing on this list can identify
          someone. Only the author sees who stood.
        </p>
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {Object.entries(composition).map(([stance, n]) => (
            <li key={stance}>
              <Badge variant="green">
                {stance}: {n}
              </Badge>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (!stands.length) return <Empty>Nobody has stood with this yet.</Empty>;

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        {data?.data.meta.total} in total. A stand carries a <em>stance</em>, and
        optionally a chip — agreeing is not one thing.
      </p>
      <ul className="space-y-2">
        {stands.map((s) => (
          <li key={s.id} className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm">
            <span className="font-medium">{s.user?.name ?? "Someone"}</span>{" "}
            <Badge variant="green">{s.stance}</Badge>{" "}
            {s.chip && <Badge variant="gray">{s.chip.replace(/_/g, " ")}</Badge>}
            <span className="text-xs text-gray-400 ml-2">
              {new Date(s.createdAt).toLocaleDateString()}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

// ── Stories ────────────────────────────────────────────────────────

function Stories({ voiceId }: { voiceId: string }) {
  const qc = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [text, setText] = useState("");
  const [anon, setAnon] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["voice-stories", voiceId],
    queryFn: () => voices.listVoiceStories(voiceId, { limit: 25 }),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["voice-stories", voiceId] });

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  const stories = data?.data.stories ?? [];

  return (
    <>
      {isAuthenticated && (
        <div className="mb-4">
          <TextAreaField
            label=""
            rows={3}
            placeholder="This happened to me too… (10 characters minimum — the server refuses shorter)"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="mt-2 flex items-center gap-3">
            <Button
              size="sm"
              isLoading={busy}
              disabled={text.trim().length < 10}
              onClick={() =>
                run(async () => {
                  await voices.addVoiceStory(voiceId, {
                    content: text.trim(),
                    isAnonymous: anon,
                  });
                  setText("");
                })
              }
            >
              Add my story
            </Button>
            <label className="text-sm text-gray-600 flex items-center gap-1.5">
              <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} />
              anonymously
            </label>
          </div>
          <Err e={err} />
        </div>
      )}

      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : !stories.length ? (
        <Empty>No stories yet.</Empty>
      ) : (
        <ul className="space-y-3">
          {stories.map((s) => (
            <StoryRow key={s.id} s={s} onAct={run} busy={busy} signedIn={isAuthenticated} />
          ))}
        </ul>
      )}
    </>
  );
}

function StoryRow({
  s,
  onAct,
  busy,
  signedIn,
}: {
  s: VoiceStory;
  onAct: (fn: () => Promise<unknown>) => Promise<void>;
  busy: boolean;
  signedIn: boolean;
}) {
  return (
    <li className="border border-gray-200 rounded-xl px-4 py-3">
      <div className="flex items-center gap-2">
        {/* user is null on an anonymous story — that is the feature, not a gap. */}
        <span className="font-medium text-gray-900">{s.user?.name ?? "Anonymous"}</span>
        {s.isFeatured && <Badge variant="purple">featured</Badge>}
        <span className="text-xs text-gray-400">
          {new Date(s.createdAt).toLocaleDateString()}
        </span>
      </div>
      <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{s.content}</p>
      <div className="flex gap-1.5 mt-2 flex-wrap items-center">
        {voices.STORY_REACTIONS.map((r) => {
          const mine = s.userReaction === r;
          return (
            <button
              key={r}
              disabled={!signedIn || busy}
              onClick={() =>
                onAct(() =>
                  mine
                    ? voices.removeVoiceStoryReaction(s.id)
                    : voices.reactToVoiceStory(s.id, r as StoryReaction),
                )
              }
              className={`text-xs px-2 py-1 rounded-full border disabled:opacity-50 ${
                mine ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 text-gray-600"
              }`}
              title={mine ? "Tap again to take it back" : ""}
            >
              {r} {s.reactionCounts?.[r] ?? 0}
            </button>
          );
        })}
        {signedIn && (
          <button
            disabled={busy}
            onClick={() => onAct(() => voices.deleteVoiceStory(s.id))}
            className="text-xs text-red-700 ml-auto disabled:opacity-50"
          >
            delete
          </button>
        )}
      </div>
    </li>
  );
}

// ── Updates ────────────────────────────────────────────────────────

function Updates({ voiceId }: { voiceId: string }) {
  const qc = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [type, setType] = useState<(typeof voices.UPDATE_TYPES)[number]>("news");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);

  // official / expert / news CLAIM a source, so the server requires one. The
  // form offered no field for it, which meant those three types could not be
  // posted from here at all — and they are the ones a ledger is made of.
  const needsSource = voices.SOURCED_UPDATE_TYPES.includes(
    type as (typeof voices.SOURCED_UPDATE_TYPES)[number],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["voice-updates", voiceId],
    queryFn: () => voices.listVoiceUpdates(voiceId, { limit: 25 }),
  });

  const updates = data?.data.updates ?? [];

  async function post() {
    setBusy(true);
    setErr(null);
    try {
      await voices.addVoiceUpdate(voiceId, {
        updateType: type,
        content: content.trim(),
        title: title.trim() || undefined,
        sourceUrl: sourceUrl.trim() || undefined,
      });
      setTitle("");
      setContent("");
      setSourceUrl("");
      await qc.invalidateQueries({ queryKey: ["voice-updates", voiceId] });
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  // Author-only on the server. The Lab offers it to anyone signed in and lets
  // the refusal do the teaching — "Only the person who raised this voice can
  // pin an update" says more than a hidden button, and on an ANONYMOUS post
  // the client cannot tell who the author is anyway: the server strips them.
  async function pin(updateId: string) {
    setBusy(true);
    setErr(null);
    setNote(null);
    try {
      await voices.pinVoiceUpdate(voiceId, updateId);
      await qc.invalidateQueries({ queryKey: ["voice-updates", voiceId] });
      setNote("Pinned. One pin per ledger — pinning this moved it off whatever held it before.");
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  async function resolve() {
    setBusy(true);
    setErr(null);
    setNote(null);
    try {
      await voices.resolveVoice(voiceId);
      await qc.invalidateQueries({ queryKey: ["voice", voiceId] });
      await qc.invalidateQueries({ queryKey: ["voice-updates", voiceId] });
      setNote("Resolved — the record now says the thing was fixed, with the pinned entry as its evidence.");
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        What has since become <strong>known</strong> — not opinion. The type is
        the claim being made about the source.
      </p>

      {isAuthenticated && (
        <div className="mb-4 space-y-2">
          <div className="flex gap-2 flex-wrap">
            <select
              value={type}
              onChange={(e) => setType(e.target.value as typeof type)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
            >
              {voices.UPDATE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title (optional)"
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-[12rem]"
            />
          </div>
          <TextAreaField
            label=""
            rows={2}
            placeholder="What is now known… (10 characters minimum)"
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <input
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            placeholder={
              needsSource
                ? `Source URL — required: a "${type}" entry claims one`
                : "Source URL (optional)"
            }
            className={`w-full border rounded-lg px-2 py-1.5 text-sm ${
              needsSource && !sourceUrl.trim() ? "border-amber-400" : "border-gray-300"
            }`}
          />
          <Button
            size="sm"
            isLoading={busy}
            disabled={content.trim().length < 10 || (needsSource && !sourceUrl.trim())}
            onClick={post}
          >
            Post update
          </Button>
          <Err e={err} />
        </div>
      )}

      {/* Pin, then resolve. The two are one move split in half: the pin marks
          WHICH entry is the outcome, and resolving reads it as evidence. The
          server refuses a resolution with no sourced pin behind it, so the
          refusal is worth provoking here rather than designing around. */}
      {isAuthenticated && updates.length > 0 && (
        <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Button size="sm" variant="secondary" isLoading={busy} onClick={resolve}>
              Mark resolved
            </Button>
            <span className="text-xs text-gray-600">
              Author only, and only with a <strong>pinned entry carrying a source</strong> —
              &ldquo;trust me, it&rsquo;s sorted&rdquo; closes nothing.
            </span>
          </div>
          {note && <p className="mt-2 text-sm text-green-800">{note}</p>}
        </div>
      )}

      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : !updates.length ? (
        <Empty>Nothing new is known yet.</Empty>
      ) : (
        <ul className="space-y-2">
          {updates.map((u: VoiceUpdate) => (
            <li key={u.id} className="border border-gray-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="blue">{u.updateType}</Badge>
                {u.isPinned && <Badge variant="purple">pinned</Badge>}
                {u.title && <span className="font-medium text-gray-900">{u.title}</span>}
                <span className="text-xs text-gray-400">
                  {new Date(u.createdAt).toLocaleDateString()}
                </span>
              </div>
              <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{u.content}</p>
              <div className="mt-1 flex items-center gap-3 flex-wrap">
                {u.sourceUrl && (
                  <a
                    href={u.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-blue-700 underline"
                  >
                    source
                  </a>
                )}
                {isAuthenticated && !u.isPinned && (
                  <button
                    disabled={busy}
                    onClick={() => pin(u.id)}
                    className="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50"
                  >
                    pin as the outcome
                  </button>
                )}
                {u.isPinned && !u.sourceUrl && (
                  <span className="text-xs text-amber-700">
                    pinned, but carries no source — resolving needs one
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ── Actions ────────────────────────────────────────────────────────

function Actions({ voiceId }: { voiceId: string }) {
  const qc = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [type, setType] = useState<(typeof voices.ACTION_TYPES)[number]>("petition");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["voice-actions", voiceId],
    queryFn: () => voices.listVoiceActions(voiceId),
  });

  const actions = data?.data.actions ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["voice-actions", voiceId] });

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        What a reader can <strong>do</strong>. `completionCount` is the only
        number here that says the voice changed anything.
      </p>

      {isAuthenticated && (
        <div className="mb-4 flex gap-2 flex-wrap items-center">
          <select
            value={type}
            onChange={(e) => setType(e.target.value as typeof type)}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          >
            {voices.ACTION_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, " ")}
              </option>
            ))}
          </select>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What to do (5 characters minimum)"
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-[14rem]"
          />
          <Button
            size="sm"
            isLoading={busy}
            disabled={title.trim().length < 5}
            onClick={() =>
              run(async () => {
                await voices.addVoiceAction(voiceId, { actionType: type, title: title.trim() });
                setTitle("");
              })
            }
          >
            Add action
          </Button>
          <Err e={err} />
        </div>
      )}

      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : !actions.length ? (
        <Empty>No actions proposed yet.</Empty>
      ) : (
        <ul className="space-y-2">
          {actions.map((a: VoiceAction) => (
            <li
              key={a.id}
              className="border border-gray-200 rounded-xl px-4 py-3 flex items-start gap-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="green">{a.actionType.replace(/_/g, " ")}</Badge>
                  <span className="font-medium text-gray-900">{a.title}</span>
                </div>
                {a.description && (
                  <p className="text-sm text-gray-700 mt-1">{a.description}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {a.completionCount} did this{a.completedByMe ? " · including you" : ""}
                </p>
                {a.templateContent && (
                  <pre className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">{a.templateContent}</pre>
                )}
              </div>
              {isAuthenticated && (
                <Button
                  size="sm"
                  variant="secondary"
                  isLoading={busy}
                  disabled={a.completedByMe}
                  onClick={() => run(() => voices.completeVoiceAction(a.id))}
                >
                  {a.completedByMe ? "Done" : "I did this"}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ── Stats + intent ─────────────────────────────────────────────────

function Stats({ voiceId }: { voiceId: string }) {
  const qc = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["voice-stats", voiceId],
    queryFn: () => voices.getVoiceEngagementStats(voiceId),
  });

  const s = data?.data.stats;

  async function setIntent(intent: (typeof voices.VOICE_INTENTS)[number]) {
    setBusy(true);
    setErr(null);
    try {
      await voices.updateVoiceIntent(voiceId, intent);
      await qc.invalidateQueries({ queryKey: ["voice"] });
    } catch (e) {
      // Only the poster may do this — a 403 here is the guard working.
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : !s ? (
        <Empty>No stats.</Empty>
      ) : (
        <div className="space-y-4 text-sm">
          <div className="flex gap-4 flex-wrap">
            <Stat label="stands" value={s.totalStands} />
            <Stat label="stories" value={s.totalStories} />
            <Stat label="comments" value={s.totalComments} />
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-1">
              How fast people showed up — the shape of attention, not its size.
            </p>
            <div className="flex gap-4 flex-wrap">
              <Stat label="first 24h" value={s.responseVelocity.first24h} />
              <Stat label="24–48h" value={s.responseVelocity.from24to48h} />
              <Stat label="after 48h" value={s.responseVelocity.after48h} />
            </div>
          </div>

          <Breakdown title="Stances" map={s.stanceBreakdown} />
          <Breakdown title="Reactions" map={s.reactionBreakdown} />

          {s.geographicSpread.length > 0 && (
            <div>
              <p className="font-medium text-gray-900 mb-1">Where from</p>
              <div className="flex gap-2 flex-wrap">
                {s.geographicSpread.map((g) => (
                  <Badge key={g.state} variant="gray">
                    {g.state}: {g.count}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {isAuthenticated && (
        <div className="mt-5 pt-4 border-t border-gray-200">
          <p className="text-sm font-medium text-gray-900">Change the intent</p>
          <p className="text-xs text-gray-500 mb-2">
            Why someone spoke changes what a good response looks like — and it is
            the thing posters most often get wrong at 2am. Poster only; a 403
            here is the guard working.
          </p>
          <div className="flex gap-1.5 flex-wrap">
            {voices.VOICE_INTENTS.map((i) => (
              <button
                key={i}
                disabled={busy}
                onClick={() => setIntent(i)}
                className="text-xs px-2.5 py-1 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                {i.replace(/_/g, " ")}
              </button>
            ))}
          </div>
          <Err e={err} />
        </div>
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-xl font-semibold text-gray-900 tabular-nums">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function Breakdown({ title, map }: { title: string; map: Record<string, number> }) {
  const entries = Object.entries(map ?? {});
  if (!entries.length) return null;
  return (
    <div>
      <p className="font-medium text-gray-900 mb-1">{title}</p>
      <div className="flex gap-2 flex-wrap">
        {entries.map(([k, n]) => (
          <Badge key={k} variant="gray">
            {k.replace(/_/g, " ")}: {n}
          </Badge>
        ))}
      </div>
    </div>
  );
}
